// Posting days, on the dashboard side.
//
// brand_profile.posting_days is the set of weekdays a client publishes on —
// 0 = Sunday … 6 = Saturday, the numbering Date#getUTCDay uses and the column
// stores. NULL / absent / empty is every day, which is every client's state
// until they pick.
//
// ── WHAT THIS IS FOR ─────────────────────────────────────────────────────────
//
// The scheduler puts posts on posting days by itself: computeNextSlotFor skips
// the rest, so nothing the server picks ever lands on a day the client turned
// off. A PERSON dragging a card is the one path that can, and this is the
// check in front of it.
//
// IT WARNS, IT DOES NOT REFUSE. A client who wants this week's post on a
// Tuesday they normally skip is not making a mistake — posting_days says where
// posts go by default, not where they are allowed to be. The backend is
// permissive for the same reason and stays that way; the daily cap is the rule
// that actually refuses, and it is unaffected by any of this.
//
// Mirrors isPostingDay in the backend's posting-days.js. Two implementations of
// one predicate is how a warning starts disagreeing with the scheduler it is
// describing, so the tolerance below is deliberately the same: anything
// unreadable becomes "every day" rather than "no days", because failing open on
// a settings field is how a client keeps publishing.

// The weekday an instant falls on, as seen on a wall clock in `tz`.
//
// Intl with weekday:'short' rather than getDay(): the browser's zone is not the
// client's, and a post at 23:30 Monday in Vancouver is already Tuesday in UTC.
// The day the client sees is the day the warning has to be about.
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

// new Date() IS NOT A SUFFICIENT GUARD, which is why this exists. `new
// Date(null)` is not Invalid Date — it is the epoch, so a missing slot would
// sail through and pop a confirm box naming a day in 1969. Only a non-blank
// string or a real Date is an instant here; everything else is "no idea",
// which the callers below turn into "say nothing".
function toInstant(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string' || !value.trim()) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

export function weekdayInZone(iso, timezone) {
  const at = toInstant(iso);
  if (!at) return null;
  try {
    const short = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' }).format(at);
    const dow = DOW[short];
    return dow === undefined ? null : dow;
  } catch {
    // An unusable timezone is not a reason to block a drag.
    return null;
  }
}

// The long day name, for the message. 'Tuesday', not 'Tue'.
export function dayNameInZone(iso, timezone) {
  const at = toInstant(iso);
  if (!at) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'long' }).format(at);
  } catch {
    return '';
  }
}

// Tolerant read of the stored column — the same shape the backend's
// normalisePostingDays forgives, and for the same reason. Only numbers and
// non-blank numeric strings: Number(null) is 0, and 0 is Sunday, so a NULL
// element would otherwise read as a day the client never picked.
export function normalisePostingDays(value) {
  if (!Array.isArray(value)) return null;
  const out = [];
  for (const raw of value) {
    let n;
    if (typeof raw === 'number') n = raw;
    else if (typeof raw === 'string' && raw.trim() !== '') n = Number(raw);
    else continue;
    if (!Number.isInteger(n) || n < 0 || n > 6) continue;
    if (!out.includes(n)) out.push(n);
  }
  return out.length ? out : null;
}

export function isPostingDay(dow, postingDays) {
  const days = normalisePostingDays(postingDays);
  return days === null || days.includes(dow);
}

// The warning to show before putting a post on `iso`, or null for no warning.
//
// null covers every case where there is nothing to say: the client posts every
// day, the target day is one of theirs, or the instant/timezone cannot be read
// at all. THE UNREADABLE CASE RETURNS null ON PURPOSE — a confirm box that
// cannot name a day is worse than no confirm box, and a settings field must
// never be able to stop someone moving their own post.
export function postingDayWarning(iso, timezone, postingDays) {
  const days = normalisePostingDays(postingDays);
  if (days === null) return null;                 // every day
  const dow = weekdayInZone(iso, timezone);
  if (dow === null) return null;                  // unreadable: say nothing
  if (days.includes(dow)) return null;            // a day they post on
  const name = dayNameInZone(iso, timezone);
  if (!name) return null;
  return `${name} isn't one of your posting days. Post anyway?`;
}

// ── Removing a posting day that already has posts on it ──────────────────────
//
// Unticking a day in Brand Hub changes where FUTURE posts get placed. It does
// nothing to posts already scheduled on that day: POST /api/brand-hub never
// touches the posts table, and the publish cron selects on scheduled_at with
// no weekday filter at all. So those posts still go out, on a day the client
// has just said they do not post on, with nothing anywhere mentioning it.
//
// That is defensible — a scheduled post is one the client already approved for
// that date — but it should not be a surprise. These functions find the posts
// that would be affected so the save can say so before it happens.
//
// NOTHING HERE MOVES OR UNSCHEDULES ANYTHING, and the save that follows a
// confirm is the ordinary save. The client is told, not managed.

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

// Which weekdays are being taken away.
//
// `previous` is what is stored, `next` is what the client has ticked. NULL on
// either side is every day — so going from Daily (NULL) to a partial set
// removes every day they did not pick, which is the most common shape of this
// and the one that would otherwise be invisible.
//
// `undefined` for `previous` means "not loaded yet" and yields nothing: a
// panel that does not know what was stored cannot know what is being removed,
// and guessing would warn about days nobody touched.
export function removedPostingDays(previous, next) {
  if (previous === undefined) return [];
  const before = normalisePostingDays(previous) || ALL_DAYS;
  const after  = normalisePostingDays(next) || ALL_DAYS;
  return before.filter(d => !after.includes(d));
}

// The future scheduled posts that sit on one of those days.
//
// THE WEEKDAY IS RESOLVED IN THE CLIENT'S ZONE, via weekdayInZone above, not
// from the UTC date in the ISO string. A post at 23:30 on a Wednesday in
// Vancouver is already Thursday in UTC, and bucketing it as Thursday would
// warn about removing a day it is not on — or stay silent about the day it is.
//
// Past posts are excluded: they have either gone out or are about to, and
// nothing said here changes them.
export function postsOnRemovedDays(posts, removed, timezone, now = new Date()) {
  if (!Array.isArray(posts) || !Array.isArray(removed) || removed.length === 0) return [];
  const from = now instanceof Date ? now.getTime() : Date.parse(now);
  return posts
    .filter(p => {
      const at = Date.parse(p?.scheduled_at);
      if (!Number.isFinite(at) || at < from) return false;
      const dow = weekdayInZone(p.scheduled_at, timezone);
      return dow !== null && removed.includes(dow);
    })
    .sort((a, b) => Date.parse(a.scheduled_at) - Date.parse(b.scheduled_at));
}

// 'Oct 1' in the client's zone.
function shortDateInZone(iso, timezone) {
  const at = toInstant(iso);
  if (!at) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timezone, month: 'short', day: 'numeric' }).format(at);
  } catch {
    return '';
  }
}

// The sentence shown before the save, or null when there is nothing to say.
//
// Singular and plural are both spelled out rather than bolted on with an (s):
// the live case is exactly one post, and "1 scheduled posts are on Thursday"
// is the kind of detail that makes a warning look automated rather than read.
export function removedDaysWarning(conflicts, timezone) {
  if (!Array.isArray(conflicts) || conflicts.length === 0) return null;

  // Day names in week order, de-duplicated — two posts on two Thursdays name
  // Thursday once.
  const dows = [...new Set(conflicts.map(p => weekdayInZone(p.scheduled_at, timezone)))]
    .filter(d => d !== null)
    .sort((a, b) => a - b);
  const names = dows.map(d => DAY_NAMES[d]).filter(Boolean);
  const dates = conflicts.map(p => shortDateInZone(p.scheduled_at, timezone)).filter(Boolean);
  if (!names.length || !dates.length) return null;

  const n      = conflicts.length;
  const noun   = n === 1 ? '1 scheduled post is' : `${n} scheduled posts are`;
  const going  = n === 1 ? "It'll still go out." : "They'll still go out.";
  return {
    count: n,
    days:  names,
    dates,
    message: `${noun} on ${listOf(names)} (${dates.join(', ')}). ${going} Save anyway?`,
  };
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// 'Thursday' / 'Thursday and Friday' / 'Tuesday, Thursday and Friday'
function listOf(items) {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
